import { Component, Inject, OnInit, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatListModule } from '@angular/material/list';

import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';

import { LocationTreeComponent } from './location-tree/location-tree.component';
import { SHARED_IMPORTS } from '../shared/shared-imports';
import { ConfirmDialogComponent } from '../shared/confirm-dialog/confirm-dialog.component';
import { ContentService } from '../../services/content.service';
import { OrderObjectByPipe } from '../../pipes/order-object-by.pipe';
import { OrderByPipe } from '../../pipes/order-by.pipe';

// Small local type so we don't need to import from the shared component
type InlineEditorOption<T> = { label: string; value: T; group?: string };

@Component({
	selector: 'location-unit-anchoring-component',
	standalone: true,
	templateUrl: 'components/location-unit-anchoring/location-unit-anchoring.component.html',
	imports: [
		...SHARED_IMPORTS,
		MatDialogModule,
		MatCardModule,
		MatListModule,
		OrderObjectByPipe,
		OrderByPipe,
		LocationTreeComponent
	],
	schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
})
export class LocationUnitAnchoringComponent implements OnInit {

	private router = inject(Router);
	private content = inject(ContentService);
	private http = inject(HttpClient);
	private dialog = inject(MatDialog);


	public getContent: (id: string, fallback?: string) => string;

	public withNavbar = true;

	public assignableUnits: any[] = [];                         // raw items you already build
	public assignableOptions: InlineEditorOption<any>[] = [];   // mapped for <app-inline-editor>

	public operatorMode = false;
	public readMode = false;
	public siteLevelActive: boolean = false;
	public locationStructure: any;
	public units: Record<string, any> = {};
	public freeUnits: Record<string, any> = {};           // ← always reassigned immutably
	public unpluggedUsb: Record<string, any> = {};        // ← always reassigned immutably
	public usbPendingPairings: Record<string, any> = {};  // ← always reassigned immutably
	public currentUser: any;
	public overlayAnch = false;

	private unitWithoutPositionTypeConst!: string;
	private usbPendingPairTypeConst!: string;
	private unpluggedUsbTypeConst!: string;


	constructor() {
		this.getContent = this.content.getContent.bind(this.content);
	}

	ngOnInit(): void {
		this.unitWithoutPositionTypeConst = this.content.getContent('LocationUnitAnchoring.UnitsWithoutPositions');
		this.usbPendingPairTypeConst = this.content.getContent('LocationUnitAnchoring.UnitPluggedIntoUsbWithoutPosition');
		this.unpluggedUsbTypeConst = this.content.getContent('LocationUnitAnchoring.UsbWithoutUnitAndPositions');

		this.getCurrentUser();
		this.initData();
	}

	private getCurrentUser(): void {
		this.http.get('/api/user/CurrentUser').subscribe((result: any) => {
			this.currentUser = result;
			this.operatorMode = this.currentUser.GlobalAppRoleId === '00000000-0000-0000-0000-000000000003';
			this.readMode = this.currentUser.GlobalAppRoleId === '00000000-0000-0000-0000-000000000004';
		});
	}

	private initData(): void {
		this.http.get<boolean>('/api/Tenant/HasSiteLevel').subscribe((hasSite) => {
			this.siteLevelActive = hasSite;

			const locationsGet = this.siteLevelActive
				? this.http.get('/api/Location/LocationOverviewWithSites')
				: this.http.get('/api/Location/LocationOverview');

			const unitsGet = this.http.get('/api/Unit/GetAll2');
			const unpluggedUsbGet = this.http.get('/api/Location/GetUnpluggedUsb');
			const usbPendingPairingsGet = this.http.get('/api/Location/GetUsbPendingPairings');

			forkJoin([locationsGet, unitsGet, unpluggedUsbGet, usbPendingPairingsGet]).subscribe((result: any[]) => {
				this.locationStructure = result[0]?.data ?? result[0] ?? [];

				// Build units + freeUnits immutably
				const allUnits = result[1]?.data ?? result[1] ?? [];
				this.units = Object.fromEntries(allUnits.map((u: any) => [u.UnitId, u]));
				this.freeUnits = { ...this.units }; // start as "all are free"

				// unpluggedUsb immutable build
				const unpluggedUsbArr = result[2]?.data ?? result[2] ?? [];
				this.unpluggedUsb = Object.fromEntries(
					unpluggedUsbArr.map((usb: any) => [usb.UsbCode, usb])
				);

				// usbPendingPairings immutable build (+ attach unit reference from freeUnits)
				const usbPending = result[3]?.data ?? result[3] ?? [];
				this.usbPendingPairings = Object.fromEntries(
					usbPending.map((p: any) => [p.UsbCode, { ...p, unit: this.freeUnits[p.UnitId] }])
				);

				// Walk nodes to mark occupied positions and decorate titles
				// Collect occupied unit ids, then prune freeUnits in one immutable step
				const occupiedUnitIds = new Set<string>();
				this.forEachNode(this.locationStructure.Roots, (node: any) => {
					if (node.NodeType === 8) {
						if (node.AnchoredUnitId !== null) {
							const u = this.freeUnits[node.AnchoredUnitId];
							node.unit = u ? { ...u, title: u.Title } : node.unit;
							if (u) occupiedUnitIds.add(node.AnchoredUnitId);
						}
						if (node.AnchoredUsbCode !== null) {
							node.unit = node.unit || {};
							node.unit.titleUSB = 'USB-' + node.AnchoredUsbCode;
						}
						if (node.unit) {
							if (node.unit.title && !node.unit.titleUSB) {
								node.unit.titleUSB = this.getContent('LocationUnitAnchoring.NoAnchor');
							}
							if (!node.unit.title && node.unit.titleUSB) {
								node.unit.title = this.getContent('LocationUnitAnchoring.NoUnitAttached');
							}
						}
					}
				});

				// Prune freeUnits immutably (remove occupied)
				this.freeUnits = Object.fromEntries(
					Object.entries(this.freeUnits).filter(([id]) => !occupiedUnitIds.has(id))
				);

				this.getAssignableUnits();     // builds assignableUnits
				this.buildAssignableOptions(); // map to editor-friendly format
			});
		});
	}

	/** Map assignableUnits -> inline editor options (label/group preserved) */
	private buildAssignableOptions(): void {
		this.assignableOptions = (this.assignableUnits || []).map(u => ({
			label: u.title,   // what appears in the dropdown
			value: u,         // hand the original object back on selection
			group: u.type,    // your 3 groups
		}));
	}

	// ---- Actions exposed to the child via outputs ----

	/** Fired by child when user clicks the small "x" to unanchor. */
	public unanchorUnit(position: any): void {
		this.overlayAnch = true;

		const ref = this.dialog.open(ConfirmDialogComponent, {
			data: {
				title: this.getContent(
					position.AnchoredUnitId
						? 'LocationUnitAnchoring.UnlinkUnitFromPosition'
						: 'LocationUnitAnchoring.UnlinkUsbFromPosition'
				),
				okText: this.getContent('Standard.Unlink'),
				cancelText: this.getContent('Standard.Cancel'),
			},
		});

		ref.afterClosed().subscribe((confirmed: boolean) => {
			if (!confirmed) return;

			if (position.unit?.UnitId) {
				if (position.AnchoredUsbCode) {
					this.http
						.post(`/api/Location/RemoveUsbUnitPairFromPosition?positionId=${position.Id}`, {})
						.subscribe({
							next: (result: any) => {
								if ((result?.status ?? 200) === 200) {
									const pair = {
										UsbCode: position.AnchoredUsbCode,
										UnitId: position.unit.UnitId,
										unit: this.units[position.unit.UnitId],
										title: this.units[position.unit.UnitId].Title + ' ~~~ ' + position.AnchoredUsbCode,
										type: this.usbPendingPairTypeConst,
									};
									this.usbPendingPairings = {
										...this.usbPendingPairings,
										[position.AnchoredUsbCode]: pair
									};

									this.assignableUnits = [...this.assignableUnits, pair];
									this.buildAssignableOptions();

									delete position.unit;
									position.AnchoredUsbCode = null;
									position.AnchoredUnitId = null;
								}
							},
							error: () => window.location.reload(),
						});
				} else {
					this.http
						.post(`/api/Location/RemoveUnitFromPosition?positionId=${position.Id}`, {})
						.subscribe({
							next: (result: any) => {
								if ((result?.status ?? 200) === 200) {
									// add back to freeUnits immutably
									this.freeUnits = {
										...this.freeUnits,
										[position.unit.UnitId]: position.unit
									};

									position.unit.type = this.unitWithoutPositionTypeConst;
									this.assignableUnits = [...this.assignableUnits, position.unit];
									this.buildAssignableOptions();

									delete position.unit;
									position.AnchoredUnitId = null;
								}
							},
							error: () => window.location.reload(),
						});
				}
			} else if (position.AnchoredUsbCode) {
				this.http
					.post(`/api/Location/RemoveUsbFromPosition?positionId=${position.Id}`, {})
					.subscribe({
						next: (result: any) => {
							if ((result?.status ?? 200) === 200) {
								const usb = {
									UsbCode: position.AnchoredUsbCode,
									title: position.AnchoredUsbCode,
									type: this.unpluggedUsbTypeConst,
								};

								// add to unpluggedUsb immutably
								this.unpluggedUsb = {
									...this.unpluggedUsb,
									[position.AnchoredUsbCode]: usb
								};

								this.assignableUnits = [...this.assignableUnits, usb];
								this.buildAssignableOptions();

								delete position.unit;
								position.AnchoredUsbCode = null;
							}
						},
						error: () => window.location.reload(),
					});
			}
		});
	}

	/** Child bubbles a pick from the inline editor; delegate to existing API flow. */
	public onAttachFromChild(ev: { unit: any; position: any }): void {
		if (!ev) return;
		this.anchorUnit(ev.unit, ev.position);
	}

	/** Optional hook if you later want the child to ask for a custom selector. */
	public openAnchorSelector(_position: any): void {
		// Intentionally left blank for now.
	}

	// ---- Parent-only actions (keep) ----

	public anchorUnit(unit: any, position: any): void {
		this.overlayAnch = true;
		const reload = () => window.location.reload();

		if (unit.UnitId) {
			if (unit.UsbCode) {
				this.http
					.post(
						`/api/Location/AssignUsbPendingPairingToPosition?positionId=${position.Id}&UsbCode=${unit.UsbCode}&unitId=${unit.UnitId}`,
						{}
					)
					.subscribe({
						next: (result: any) => {
							if ((result?.status ?? 200) === 200) {
								position.AnchoredUnitId = unit.UnitId;
								position.AnchoredUsbCode = unit.UsbCode;
								position.unit = unit;
								position.unit.titleUSB = 'USB-' + unit.UsbCode;
								position.unit.title = unit.unit.Title;

								// remove from usbPendingPairings immutably
								const { [unit.UsbCode]: _removedPair, ...restPairs } = this.usbPendingPairings;
								this.usbPendingPairings = restPairs;

								this.assignableUnits = this.assignableUnits.filter((el) => el !== unit);
								this.assignableUnits = [...this.assignableUnits];
								this.buildAssignableOptions();
							}
						},
						error: reload,
					});
			} else {
				this.http
					.post(`/api/Location/AssignUnitToPosition?positionId=${position.Id}&unitId=${unit.UnitId}`, {})
					.subscribe({
						next: (result: any) => {
							if ((result?.status ?? 200) === 200) {
								position.AnchoredUnitId = unit.UnitId;
								position.unit = unit;
								position.unit.titleUSB = this.getContent('LocationUnitAnchoring.NoAnchor');

								// remove from freeUnits immutably
								const { [unit.UnitId]: _removed, ...rest } = this.freeUnits;
								this.freeUnits = rest;

								this.assignableUnits = this.assignableUnits.filter((el) => el !== unit);
								this.assignableUnits = [...this.assignableUnits];
								this.buildAssignableOptions();
							}
						},
						error: reload,
					});
			}
		} else if (unit.UsbCode) {
			this.http
				.post(`/api/Location/AssignUnpluggedUsbToPosition?positionId=${position.Id}&UsbCode=${unit.UsbCode}`, {})
				.subscribe({
					next: (result: any) => {
						if ((result?.status ?? 200) === 200) {
							position.AnchoredUsbCode = unit.UsbCode;
							position.unit = unit;
							position.unit.titleUSB = 'USB-' + unit.UsbCode;
							position.unit.title = this.getContent('LocationUnitAnchoring.NoUnitAttached');

							// remove from unpluggedUsb immutably
							const { [unit.UsbCode]: _removedUsb, ...restUsb } = this.unpluggedUsb;
							this.unpluggedUsb = restUsb;

							this.assignableUnits = this.assignableUnits.filter((el) => el !== unit);
							this.assignableUnits = [...this.assignableUnits];
							this.buildAssignableOptions();
						}
					},
					error: reload,
				});
		}
	}

	public getAssignableUnits(): void {

		// Pairings → assignable & remove their units from freeUnits (immutably once)
		const newAssignable: any[] = [];
		let nextFreeUnits = { ...this.freeUnits };

		Object.values(this.usbPendingPairings).forEach((pairing: any) => {
			pairing.type = this.usbPendingPairTypeConst;
			pairing.title = pairing.unit.Title + ' ~~~ ' + pairing.UsbCode;
			newAssignable.push(pairing);

			const { [pairing.unit.UnitId]: _rm, ...rest } = nextFreeUnits;
			nextFreeUnits = rest;
		});
		this.assignableUnits = [...this.assignableUnits, ...newAssignable];
		this.freeUnits = nextFreeUnits; // single immutable assignment

		// Free units → assignable
		const freeAssignable = Object.values(this.freeUnits).map((u: any) => ({
			...u,
			type: this.unitWithoutPositionTypeConst,
			title: u.Title,
		}));
		this.assignableUnits = [...this.assignableUnits, ...freeAssignable];

		// Unplugged USB → assignable
		const unpluggedAssignable = Object.values(this.unpluggedUsb).map((usb: any) => ({
			...usb,
			type: this.unpluggedUsbTypeConst,
			title: usb.UsbCode,
		}));
		this.assignableUnits = [...this.assignableUnits, ...unpluggedAssignable];

		// Keep options in sync
		this.buildAssignableOptions();
	}

	public goBack(): void {
		this.router.navigate(['/Main']);
	}

	trackByUnitId = (_: number, u: any) => u?.UnitId ?? u?.Id ?? _;
	trackByPairUsbCode = (_: number, p: any) => p?.UsbCode ?? _;
	trackByUsbCode = (_: number, u: any) => u?.UsbCode ?? _;

	// Utility used only by the parent during initial normalization
	private forEachNode(arr: any[], callback: (n: any) => void): void {
		if (!Array.isArray(arr)) return;
		arr.forEach((value) => {
			if (value.Children) {
				this.forEachNode(value.Children, callback);
			}
			callback(value);
		});
	}
}
